/* eslint-disable no-plusplus */
import ShopifyService from './ajax-service';

export const CART_EVENTS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  LOADING: 'LOADING',
  FINISHED: 'FINISHED',
};

class CartService {
  constructor() {
    this.lastAddedItemOrigin = null;
    this.lastComponentName = null;
    this.cart = {
      id: null,
      subtotal: {
        amount: 0,
        currencyCode: window.TMW?.localization?.currencyCode,
      },
      lines: [],
    };
    this.loading = false;
    this.observers = [];

    this.get();

    // For backwards compatibility we will listen for events
    // However, we should avoid using events and import/call methods on CartService directly
    document.addEventListener('cart:add', (e) => {
      this.add(e.detail?.payload);
      if (e.detail?.origin) {
        this.lastAddedItemOrigin = e.detail.origin;
      }
      if (e.detail?.component_name) {
        this.lastComponentName = e.detail.component_name;
      }
    });
    document.addEventListener('cart:updateAttributes', (e) => {
      this.updateAttributes(e.detail?.payload);
    });
    document.addEventListener('cart:add-complete', ({ detail }) => {
      const { json, body } = detail;
      for (let i = 0; i < json.items.length; i++) {
        const item = json.items[i];
        const payload = body.items.find(
          (p) => Number(p.id) === Number(item.id),
        );
        if (payload) {
          item.quantity = payload.quantity;
          this.callDataLayer(
            item,
            this.lastAddedItemOrigin,
            this.lastComponentName,
          );
        }
      }
    });
    this.mode = ShopifyService.name;
  }

  async callShopify(method, event, ...params) {
    this.loading = true;
    this.notify(CART_EVENTS.LOADING);
    const cart = await ShopifyService[method](this.cart, ...params);
    if (cart) this.cart = cart;
    if (event) this.notify(event);
    this.loading = false;
    this.notify(CART_EVENTS.FINISHED);
    return cart;
  }

  async get(silent = false) {
    return this.callShopify('get', silent ? null : CART_EVENTS.CREATE);
  }

  async add(lines, silent = false) {
    return this.callShopify('add', silent ? null : CART_EVENTS.UPDATE, lines);
  }

  async remove(lines, silent = false) {
    return this.callShopify(
      'remove',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async update(lines, silent = false) {
    return this.callShopify(
      'update',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async updateAttributes(attributes, silent = true) {
    return this.callShopify(
      'updateAttributes',
      silent ? null : CART_EVENTS.UPDATE,
      attributes,
    );
  }

  notify(event) {
    this.observers.forEach((o) => o.call(o, event));
  }

  addListener(observer) {
    this.observers.push(observer);
  }

  removeListener(observer) {
    this.observers = this.observers.filter((o) => o !== observer);
  }

  callDataLayer(item, origin, componentName) {
    const itemData = {
      item_name: item.product_title,
      item_id: item.sku,
      item_variant: item.variant_title,
      item_variant_internal_id: item.variant_id,
      quantity: item.quantity,
      parent_internal_id: item.product_id,
      parent_item_name: item.product_title,
      item_brand: item.vendor,
      handle: item.handle,
      price: item.price / 100,
      availability: true,
      product_type: item.product_type,
    };

    if (item.properties?._item_list_id) {
      itemData.item_list_id = item.properties._item_list_id;
      itemData.item_list_name = item.properties._item_list_name;
    }

    const payload = {
      event: 'add_to_cart',
      value: item.discounted_price / 100,
      currency: Shopify.currency.active,
      origin,
      page_location: window.location.href,
      items: [itemData],
    };

    if (componentName) {
      payload.component_name = componentName;
    }

    window.dataLayer.push(payload);
    this.lastAddedItemOrigin = null;
    this.lastComponentName = null;
  }
}

const CartServiceSingleton = new CartService();
export default CartServiceSingleton;
